import { getCatalogAPI } from 'api/endpoints/catalog/catalog.api';
import * as CommercialProfileTypes from './commercialProfile.types';
import { initialProductsQuantity, showMoreProducts } from 'utils/constants';
import { catalogError, commercialProfileError } from 'utils/errorCodes';
import { notificationErr } from 'views/components/notification';
import { messageError, messageSuccess } from 'views/components/message';
import {
  IBodyProductSearch,
  TQuerySearchArray,
  TSortBy,
} from 'interfaces/catalog.interface';
import { IBodyMultimediaFetch } from 'interfaces/multimedia.interface';
import {
  addMediaToProfileAPI,
  addProductsToProfileAPI,
} from 'api/endpoints/commercialProfile/commercialProfile.api';
import { findMultimediaAPI } from 'api/endpoints/multimedia/multimedia.api';
import {
  bannerType,
  videoType,
} from 'views/pages/main/multimediaPage/components/constants';
import { TinitialCommercialProfileState } from 'interfaces/commercialProfile.interface';
import {
  getCompanyInfoAPI,
  updateTenantDataAPI,
} from 'api/endpoints/company/company.api';
import { IfindMultimediaAPIItemResponse } from 'api/endpoints/multimedia/multimedia.response.interface';
import { ITranslate } from 'interfaces/general.interface';
import { IupdateTenantDataAPIBody } from 'api/endpoints/company/company.response.interface';
import { Dispatch } from 'react';
import { IgetCatalogResponseDataItem } from 'api/endpoints/catalog/catalog.response.interface';

export async function getComercialProfileDataAction(
  dispatch: Dispatch<any>,
  brandId: string,
  lanuageId: string,
  translate: ITranslate,
  token: string
) {
  let multimediaBody: IBodyMultimediaFetch = {
    options: {
      profile: true,
      brand_id: [brandId],
      multimedia_type: [],
      search: [],
      limit: 100,
      index: 0,
    },
    filter: {
      tag: [],
      product: [],
      division: [],
      gender: [],
      country: [],
      season: [],
    },
  };

  // Get banners
  try {
    multimediaBody.options.multimedia_type = [bannerType];
    const getBannersFetch = await findMultimediaAPI(multimediaBody, undefined, token);
    if (getBannersFetch.response.status === 200) {
      dispatch({
        type: CommercialProfileTypes.SET_BANNER,
        payload: getBannersFetch.data[0],
      });
    }
  } catch (error) {
    console.log(error);
  }

  // Get videos
  try {
    multimediaBody.options.multimedia_type = [videoType];
    const getVideosFetch = await findMultimediaAPI(multimediaBody, undefined, token);
    if (getVideosFetch.response.status === 200) {
      dispatch({
        type: CommercialProfileTypes.SET_VIDEOS,
        payload: getVideosFetch.data,
      });
    }
  } catch (error) {
    console.log(error);
  }

  const body: IBodyProductSearch = {
    options: {
      brand_id: [brandId],
      favorite: '',
      group: [],
      with_images: null,
      profile: true,
      search: [],
      language_id: lanuageId,
      order: [],
      limit: 20,
      index: 0,
    },
    filter: {
      reference: [],
      color: [],
      season: [],
      segmentation: [],
      division: [],
      family: [],
      gender: [],
      tags: [],
      tiers: [],
      pvi: [],
      pvpr: [],
    },
  };

  // Get products
  try {
    const getProductsFetch = await getCatalogAPI({ body, token });
    if (getProductsFetch.response.status === 200) {
      dispatch({
        type: CommercialProfileTypes.SET_PRODUCTS,
        payload: getProductsFetch.data.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

export async function setBannerAction(
  dispatch: Dispatch<any>,
  banner: IfindMultimediaAPIItemResponse,
  state: TinitialCommercialProfileState,
  translate: ITranslate,
  token: string
) {
  try {
    let multimediaBody = [{ id: banner.id, profile: true }];
    if (state.banner?.id) {
      multimediaBody.push({
        id: state.banner.id,
        profile: false,
      });
    }
    const addMediaToProfileFetch = await addMediaToProfileAPI(multimediaBody, token);
    if (addMediaToProfileFetch.response.status === 200) {
      dispatch({
        type: CommercialProfileTypes.SET_BANNER,
        payload: banner,
      });
      return messageSuccess(
        translate('commercial-proile_actions_set-banner-action-success')
      );
    } else {
      messageError(translate('commercial-proile_actions_set-banner-action-error'));
    }
  } catch (err) {
    console.log(err);
    messageError(translate('commercial-proile_actions_set-banner-action-server-error'));
  }
}

export async function initBioAction(
  dispatch: Dispatch<any>,
  tenantId: string,
  countryId: string,
  translate: ITranslate,
  token: string
) {
  try {
    const companyFetch = await getCompanyInfoAPI({ tenantId, countryId, token });
    if (companyFetch.response.status === 200) {
      return dispatch({
        type: CommercialProfileTypes.SET_BIO,
        payload: companyFetch.data?.bio ?? '',
      });
    }
    if (companyFetch.response.status === 404) {
      return dispatch({
        type: CommercialProfileTypes.SET_BIO,
        payload: '',
      });
    }
    messageError(translate('commercial-profile_actions_init-bio_error'));
    return notificationErr(commercialProfileError.init_bio_002, translate);
  } catch (err) {
    messageError(translate('commercial-profile_actions_init-bio_error'));
    return notificationErr(commercialProfileError.init_bio_001, translate);
  }
}

export async function updateBioAction(
  dispatch: Dispatch<any>,
  bio: string,
  tenantId: string,
  translate: ITranslate,
  token: string
) {
  try {
    const body: IupdateTenantDataAPIBody = {
      filter: { id: tenantId },
      update: { bio: bio },
    };
    const companyFetch = await updateTenantDataAPI({ body, token });
    if (companyFetch.response.status === 200) {
      messageSuccess(translate('commercial-profile_actions_update-bio_success'));
      return dispatch({
        type: CommercialProfileTypes.SET_BIO,
        payload: bio,
      });
    } else {
      messageError(translate('commercial-profile_actions_update-bio_error'));
      return notificationErr(commercialProfileError.update_bio_002, translate);
    }
  } catch (err) {
    messageError(translate('commercial-profile_actions_update-bio_error'));
    return notificationErr(commercialProfileError.update_bio_001, translate);
  }
}

export async function setProductsAction(
  dispatch: Dispatch<any>,
  products: IgetCatalogResponseDataItem[],
  state: TinitialCommercialProfileState,
  translate: ITranslate,
  token: string
) {
  let productsBody = products.map((product) => ({ id: product.id, profile: true }));
  let productsBodyIds = products.map((product) => product.id);
  for (const stateProduct of state.products) {
    if (!productsBodyIds.includes(stateProduct.id)) {
      productsBody.push({ id: stateProduct.id, profile: false });
    }
  }
  try {
    const addProductsToProfileFetch = await addProductsToProfileAPI(productsBody, token);
    if (addProductsToProfileFetch.response.status === 200) {
      dispatch({
        type: CommercialProfileTypes.SET_PRODUCTS,
        payload: products,
      });
      return messageSuccess(
        translate('commercial-proile_actions_set-products-action-success')
      );
    } else {
      messageError(translate('commercial-proile_actions_set-products-action-error'));
    }
  } catch (err) {
    console.log(err);
    messageError(translate('commercial-proile_actions_set-products-action-server-error'));
  }
}

export async function removeProductAction(
  dispatch: Dispatch<any>,
  product: IgetCatalogResponseDataItem,
  translate: ITranslate,
  token: string
) {
  try {
    const removeProductFromProfileFetch = await addProductsToProfileAPI(
      [{ id: product.id, profile: false }],
      token
    );
    if (removeProductFromProfileFetch.response.status === 200) {
      return dispatch({
        type: CommercialProfileTypes.REMOVE_PRODUCT,
        payload: product,
      });
    } else {
      messageError(translate('commercial-proile_actions_remove-product-action-error'));
    }
  } catch (err) {
    console.log(err);
    messageError(
      translate('commercial-proile_actions_remove-product-action-server-error')
    );
  }
}

export async function setVideosAction(
  dispatch: Dispatch<any>,
  videos: IfindMultimediaAPIItemResponse[],
  state: TinitialCommercialProfileState,
  translate: ITranslate,
  token: string
) {
  let multimediaBody = videos.map((video) => ({ id: video.id, profile: true }));
  let multimediaBodyIds = videos.map((video) => video.id);
  for (const stateVideo of state.videos) {
    if (!multimediaBodyIds.includes(stateVideo.id)) {
      multimediaBody.push({ id: stateVideo.id, profile: false });
    }
  }
  try {
    const addMediaToProfileFetch = await addMediaToProfileAPI(multimediaBody, token);
    if (addMediaToProfileFetch.response.status === 200) {
      dispatch({
        type: CommercialProfileTypes.SET_VIDEOS,
        payload: videos,
      });
      return messageSuccess(
        translate('commercial-proile_actions_set-videos-action-success')
      );
    } else {
      messageError(translate('commercial-proile_actions_set-videos-action-error'));
    }
  } catch (err) {
    console.log(err);
    messageError(translate('commercial-proile_actions_set-videos-action-server-error'));
  }
}

export async function removeVideoAction(
  dispatch: Dispatch<any>,
  video: IfindMultimediaAPIItemResponse,
  translate: ITranslate,
  token: string
) {
  try {
    const removeMediaToProfileFetch = await addMediaToProfileAPI(
      [{ id: video.id, profile: false }],
      token
    );
    if (removeMediaToProfileFetch.response.status === 200) {
      return dispatch({
        type: CommercialProfileTypes.REMOVE_VIDEO,
        payload: video,
      });
    } else {
      messageError(translate('commercial-proile_actions_remove-video-action-error'));
    }
  } catch (err) {
    messageError(translate('commercial-proile_actions_remove-video-action-server-error'));
    console.log(err);
  }
}

// Catalog
export async function getCatalogAction(
  dispatch: Dispatch<any>,
  bodyProductSearch: IBodyProductSearch,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  dispatch({ type: CommercialProfileTypes.SET_IS_LOADING, payload: true });
  try {
    bodyProductSearch.options.index = 0;
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
      payload: bodyProductSearch,
    });
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
      payload: bodyProductSearch,
    });
    const products = await getAndOrderImagesCatalog(
      dispatch,
      bodyProductSearch,
      signal,
      token
    );
    if (products) {
      dispatch({
        type: CommercialProfileTypes.INCREMENT_PAGINATION_INDEX,
        payload: bodyProductSearch.options.index + showMoreProducts,
      });
      return dispatch({
        type: CommercialProfileTypes.GET_CATALOG,
        payload: products,
      });
    }
  } catch (err) {
    console.log(err);
    // return notificationErr(catalogError.get_catalog_001, translate)
  } finally {
    dispatch({ type: CommercialProfileTypes.SET_IS_LOADING, payload: false });
  }
}

export async function addMoreProductsToCatalogAction(
  dispatch: Dispatch<any>,
  bodyProductSearch: any,
  translate: ITranslate,
  token: string
) {
  if (bodyProductSearch.options.index === 0) {
    bodyProductSearch.options.index = initialProductsQuantity;
  }
  dispatch({ type: CommercialProfileTypes.SET_IS_LOADING_MORE_PRODUCTS, payload: true });
  dispatch({
    type: CommercialProfileTypes.INCREMENT_PAGINATION_INDEX,
    payload: bodyProductSearch.options.index + showMoreProducts,
  });
  try {
    const products = await getAndOrderImagesCatalog(
      dispatch,
      bodyProductSearch,
      undefined,
      token
    );
    if (products) {
      return dispatch({
        type: CommercialProfileTypes.ADD_MORE_TO_CATALOG,
        payload: products,
      });
    }
  } catch (err) {
    // return notificationErr(catalogError.add_more_products_to_catalog_001, translate)
  } finally {
    dispatch({
      type: CommercialProfileTypes.SET_IS_LOADING_MORE_PRODUCTS,
      payload: false,
    });
  }
}

// Filters
export async function filterAction(
  dispatch: Dispatch<any>,
  setOpenDrawer: any,
  commercialProfileState: any,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  setOpenDrawer(false);
  dispatch({ type: CommercialProfileTypes.SET_IS_LOADING, payload: true });
  dispatch({ type: CommercialProfileTypes.INCREMENT_PAGINATION_INDEX, payload: 0 });
  commercialProfileState.bodyProductSearch.options.index = 0;
  commercialProfileState.bodyProductSearch = commercialProfileState.bodyProductSearchTemp;
  dispatch({
    type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
    payload: commercialProfileState.bodyProductSearchTemp,
  });
  dispatch({
    type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
    payload: commercialProfileState.bodyProductSearch,
  });
  try {
    const products = await getAndOrderImagesCatalog(
      dispatch,
      commercialProfileState.bodyProductSearch,
      signal,
      token
    );
    if (products) {
      return dispatch({
        type: CommercialProfileTypes.GET_CATALOG,
        payload: products,
      });
    } else {
      return dispatch({
        type: CommercialProfileTypes.GET_CATALOG,
        payload: [],
      });
    }
  } catch (err) {
    return notificationErr(catalogError.filter_catalog_001, translate);
  } finally {
    dispatch({ type: CommercialProfileTypes.SET_IS_LOADING, payload: false });
  }
}

export async function addBodyFiltersAction(
  dispatch: Dispatch<any>,
  field: string,
  value: string
) {
  return dispatch({
    type: CommercialProfileTypes.ADD_BODY_FILTERS,
    payload: {
      field,
      value,
    },
  });
}

export async function removeBodyFiltersAction(
  dispatch: Dispatch<any>,
  field: string,
  value: string
) {
  return dispatch({
    type: CommercialProfileTypes.REMOVE_BODY_FILTERS,
    payload: {
      field,
      value,
    },
  });
}

export async function resetBodyFiltersAction(
  dispatch: Dispatch<any>,
  bodyProductSearch: IBodyProductSearch,
  setOpenDrawer: any,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  setOpenDrawer && setOpenDrawer(false);
  bodyProductSearch.filter.color = [];
  bodyProductSearch.filter.season = [];
  bodyProductSearch.filter.segmentation = [];
  bodyProductSearch.filter.division = [];
  bodyProductSearch.filter.family = [];
  bodyProductSearch.filter.gender = [];
  bodyProductSearch.filter.pvi = [];
  bodyProductSearch.filter.pvpr = [];
  bodyProductSearch.options.favorite = '';
  dispatch({
    type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
    payload: bodyProductSearch,
  });
  dispatch({
    type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
    payload: bodyProductSearch,
  });
  getCatalogAction(dispatch, bodyProductSearch, translate, signal, token);
  dispatch({ type: CommercialProfileTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: true });
  dispatch({ type: CommercialProfileTypes.SET_IS_EMPTY_PRODUCTS, payload: false });
  return dispatch({ type: CommercialProfileTypes.RESET_BODY_FILTERS });
}

export async function setPriceSlidersAction(
  dispatch: Dispatch<any>,
  field: string,
  value: string
) {
  return dispatch({
    type: CommercialProfileTypes.SET_PRICE_SLIDERS,
    payload: {
      field,
      value,
    },
  });
}

export async function setFavoritesAction(dispatch: Dispatch<any>, userId: string | null) {
  dispatch({ type: CommercialProfileTypes.INCREMENT_PAGINATION_INDEX, payload: 0 });
  return dispatch({ type: CommercialProfileTypes.SET_FAVORITES, payload: userId });
}

export async function resetPriceSliderAction(
  dispatch: Dispatch<any>,
  type: string,
  bodyProductSearch: any,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  dispatch({ type: CommercialProfileTypes.RESET_PRICE_SLIDER, payload: { type: type } });
  bodyProductSearch.filter[type] = [];
  getCatalogAction(dispatch, bodyProductSearch, translate, signal, token);
}

// Search Engine //////////////////////////////////////////////////////
export async function setQuerySearchAction(dispatch: Dispatch<any>, query: string) {
  return dispatch({ type: CommercialProfileTypes.SET_QUERY_SEARCH, payload: query });
}

export async function addToBodySearchAction(
  dispatch: Dispatch<any>,
  query: string,
  commercialProfileState: TinitialCommercialProfileState,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  try {
    commercialProfileState.bodyProductSearch.options.index = 0;
    commercialProfileState.bodyProductSearch.options.search.push(`%${query}%`);
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
      payload: commercialProfileState.bodyProductSearch,
    });
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
      payload: commercialProfileState.bodyProductSearch,
    });
    getCatalogAction(
      dispatch,
      commercialProfileState.bodyProductSearch,
      translate,
      signal,
      token
    );
  } catch (err) {
    console.log(err);
  }
}

export async function removeFromBodySearchAction(
  dispatch: Dispatch<any>,
  query: string,
  commercialProfileState: TinitialCommercialProfileState,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  try {
    commercialProfileState.bodyProductSearch.options.index = 0;
    const filteredSearch = commercialProfileState.bodyProductSearch.options.search.filter(
      (item: any) => item !== query
    );
    commercialProfileState.bodyProductSearch.options.search = filteredSearch;
    commercialProfileState.bodyProductSearchTemp =
      commercialProfileState.bodyProductSearch;
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
      payload: commercialProfileState.bodyProductSearch,
    });
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
      payload: commercialProfileState.bodyProductSearch,
    });
    getCatalogAction(
      dispatch,
      commercialProfileState.bodyProductSearch,
      translate,
      signal,
      token
    );
  } catch (err) {
    console.log(err);
  }
}

export async function removeAllFromBodySearchAction(
  dispatch: Dispatch<any>,
  commercialProfileState: TinitialCommercialProfileState,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  try {
    commercialProfileState.bodyProductSearch.options.index = 0;
    commercialProfileState.bodyProductSearch.options.search = [];
    commercialProfileState.bodyProductSearch.options.favorite = '';
    commercialProfileState.bodyProductSearch.filter = {
      reference: [],
      color: [],
      season: [],
      segmentation: [],
      division: [],
      family: [],
      gender: [],
      pvi: [],
      pvpr: [],
      tags: [],
      tiers: [],
    };
    commercialProfileState.bodyProductSearchTemp =
      commercialProfileState.bodyProductSearch;
    commercialProfileState.querysearchArray = [];
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
      payload: commercialProfileState.bodyProductSearch,
    });
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
      payload: commercialProfileState.bodyProductSearch,
    });
    getCatalogAction(
      dispatch,
      commercialProfileState.bodyProductSearch,
      translate,
      signal,
      token
    );
  } catch (err) {
    console.log(err);
  }
}

export async function removeFromBodySearchFilterAction(
  dispatch: Dispatch<any>,
  value: any,
  commercialProfileState: any,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  const { tag, field } = value;
  try {
    commercialProfileState.bodyProductSearch.options.index = 0;
    value.value.forEach((id: string) => {
      commercialProfileState.bodyProductSearch.filter[field] =
        commercialProfileState.bodyProductSearch.filter[field].filter(
          (item: any) => item !== id
        );
    });
    const filteredQuerySearchArray = commercialProfileState.querysearchArray.filter(
      (item: any) => item.tag !== tag
    );
    dispatch({
      type: CommercialProfileTypes.SET_BODY_SEARCH_FILTER,
      payload: {
        querySearchArray: filteredQuerySearchArray,
      },
    });
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
      payload: commercialProfileState.bodyProductSearch,
    });
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
      payload: commercialProfileState.bodyProductSearch,
    });
    getCatalogAction(
      dispatch,
      commercialProfileState.bodyProductSearch,
      translate,
      signal,
      token
    );
  } catch (err) {
    console.log(err);
  }
}

export async function addToQuerySearchArrayAction(
  dispatch: Dispatch<any>,
  value: TQuerySearchArray
) {
  return dispatch({
    type: CommercialProfileTypes.ADD_TO_QUERY_SEARCH_ARRAY,
    payload: value,
  });
}

export async function removeFromQuerySearchArrayAction(
  dispatch: Dispatch<any>,
  value: TQuerySearchArray
) {
  return dispatch({
    type: CommercialProfileTypes.REMOVE_FROM_QUERY_SEARCH_ARRAY,
    payload: value,
  });
}

// Sort By //////////////////////////////////////////////////////
export async function setSortByAction(
  dispatch: Dispatch<any>,
  sortBy: string,
  bodyProductSearch: IBodyProductSearch,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  bodyProductSearch.options.order = [sortBy as TSortBy];
  dispatch({ type: CommercialProfileTypes.SET_IS_LOADING, payload: true });
  dispatch({ type: CommercialProfileTypes.SET_SORT_BY, payload: sortBy });
  dispatch({
    type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
    payload: bodyProductSearch,
  });
  dispatch({
    type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
    payload: bodyProductSearch,
  });
  try {
    const products = await getAndOrderImagesCatalog(
      dispatch,
      bodyProductSearch,
      signal,
      token
    );
    if (products) {
      return dispatch({
        type: CommercialProfileTypes.GET_CATALOG,
        payload: products,
      });
    }
  } catch (error) {
    console.log('error');
  } finally {
    return dispatch({ type: CommercialProfileTypes.SET_IS_LOADING, payload: false });
  }
}

export async function setSortByHaveImagesAction(
  dispatch: Dispatch<any>,
  haveImages: boolean | null,
  bodyProductSearch: IBodyProductSearch,
  translate: ITranslate,
  signal: AbortSignal | undefined,
  token: string
) {
  dispatch({ type: CommercialProfileTypes.SET_IS_LOADING, payload: true });
  try {
    bodyProductSearch.options.with_images = haveImages;
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH,
      payload: bodyProductSearch,
    });
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
      payload: bodyProductSearch,
    });
    await getCatalogAction(dispatch, bodyProductSearch, translate, signal, token);
  } catch (error) {
    console.log(error);
  } finally {
    return dispatch({ type: CommercialProfileTypes.SET_IS_LOADING, payload: false });
  }
}

////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////

async function getAndOrderImagesCatalog(
  dispatch: Dispatch<any>,
  body: any,
  signal: AbortSignal | undefined,
  token: string
) {
  const catalogFetch = await getCatalogAPI({ body, token, signal });
  dispatch({ type: CommercialProfileTypes.SET_IS_EMPTY_PRODUCTS, payload: false });
  if (catalogFetch.response.status === 404 || catalogFetch.data.data.length === 0) {
    dispatch({ type: CommercialProfileTypes.SET_IS_EMPTY_PRODUCTS, payload: true });
    return {
      count: 0,
      data: [],
    };
    // return dispatch({ type: CommercialProfileTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: false })
  }
  if (catalogFetch.data.data.length < showMoreProducts) {
    dispatch({
      type: CommercialProfileTypes.SET_THERE_ARE_MORE_PRODUCTS,
      payload: false,
    });
  } else {
    dispatch({ type: CommercialProfileTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: true });
  }
  const products = catalogFetch.data.data;
  products.forEach(async (item) => {
    if (item?.images && item?.images?.length > 0) {
      item.images.reverse();
      item.images.forEach((element, index) => {
        const ext = getExtension(element);
        const getImageNumber = ext.split('.')[0];
        if (getImageNumber[1] === '1' && Number(getImageNumber[0]) !== 1) {
          item.images.slice(index, 1);
          item.images.unshift(element);
        }
        const result = item.images.reduce((acc: string[], item) => {
          if (!acc.includes(item)) {
            acc.push(item);
          }
          return acc;
        }, []);
        item.images = result;
      });
    }
  });
  return {
    count: catalogFetch.data.count,
    data: products,
  };
}

function getExtension(url: string) {
  const filename = url
    .substring(1 + url.lastIndexOf('/'))
    .split('?')[0]
    .split('#')[0];
  return filename.substring(filename.lastIndexOf('.'));
}
